import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Mail, Map, Image, Settings, LogOut, ChevronRight, BadgeDollarSign, TestTube, Handshake, Menu, X, Type } from "lucide-react";
import ConsoleOverview from "@/components/console/ConsoleOverview";
import ConsoleGrowers from "@/components/console/ConsoleGrowers";
import AdminApplications from "@/components/AdminApplications";
import ConsoleSubscribers from "@/components/console/ConsoleSubscribers";
import ConsoleSubscriptions from "@/components/console/ConsoleSubscriptions";
import ConsoleAreaMap from "@/components/console/ConsoleAreaMap";
import ConsoleMedia from "@/components/console/ConsoleMedia";
import ConsoleEmailOutbox from "@/components/console/ConsoleEmailOutbox";
import ConsoleEtchery from "@/components/console/ConsoleEtchery";
import ConsoleSiteContent from "@/components/console/ConsoleSiteContent";
import ConsoleTesting from "@/components/console/ConsoleTesting";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const ALL_SECTIONS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, component: ConsoleOverview },
  { id: "growers", label: "Growers", icon: Users, component: ConsoleGrowers },
  { id: "applications", label: "Applications", icon: FileText, component: AdminApplications },
  { id: "subscriptions", label: "Subscriptions", icon: BadgeDollarSign, component: ConsoleSubscriptions },
  { id: "subscribers", label: "Waitlist", icon: Mail, component: ConsoleSubscribers },
  { id: "territory", label: "Territory Hub", icon: Map, component: ConsoleAreaMap },
  { id: "etchery", label: "Etchery Partnership", icon: Handshake, component: ConsoleEtchery },
  { id: "site-content", label: "Site Content", icon: Type, component: ConsoleSiteContent },
  { id: "media", label: "Media Library", icon: Image, component: ConsoleMedia },
  { id: "emails", label: "Email Outbox", icon: Mail, component: ConsoleEmailOutbox },
  { id: "testing", label: "Test Mode", icon: TestTube, component: ConsoleTesting },
];

// Etchery partner role only sees these section ids.
const ETCHERY_ALLOWED = new Set(["etchery"]);

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isEtchery = user?.role === "etchery";
  const SECTIONS = isEtchery
    ? ALL_SECTIONS.filter((s) => ETCHERY_ALLOWED.has(s.id))
    : ALL_SECTIONS;
  const [active, setActive] = useState(isEtchery ? "etchery" : "overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setDrawerOpen(false);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const Section = (SECTIONS.find((s) => s.id === active) || SECTIONS[0]).component;
  const activeLabel = (SECTIONS.find((s) => s.id === active) || SECTIONS[0]).label;

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const pickSection = (id) => {
    setActive(id);
    setDrawerOpen(false);
  };

  const sidebarBody = (
    <>
      <div className="p-6 border-b border-[var(--bloom-border)]">
        <button onClick={() => navigate("/")} className="flex items-center gap-3 w-full text-left" data-testid="console-home">
          <img src={BRAND_ASSETS.logo_circle} alt="" className="w-11 h-11 object-contain" />
          <div className="min-w-0">
            <div className="font-serif text-base leading-tight uppercase tracking-[0.04em]">The Club</div>
            <div className="font-serif italic text-sm leading-tight text-[var(--bloom-ink-soft)]">Console</div>
          </div>
        </button>
      </div>

      <nav className="flex-1 p-3 overflow-y-auto" data-testid="console-nav">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = s.id === active;
          return (
            <button
              key={s.id}
              onClick={() => pickSection(s.id)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 mb-1 transition ${
                isActive
                  ? "bg-[var(--bloom-ink)] text-[#FAF4FA]"
                  : "text-[var(--bloom-ink-soft)] hover:bg-[var(--bloom-surface)] hover:text-[var(--bloom-ink)]"
              }`}
              style={{ borderRadius: 6 }}
              data-testid={`console-tab-${s.id}`}
            >
              <span className="flex items-center gap-3">
                <Icon size={16} strokeWidth={1.5} />
                <span className="font-serif text-base">{s.label}</span>
              </span>
              {isActive && <ChevronRight size={14} />}
            </button>
          );
        })}
      </nav>

      <div className="p-4 border-t border-[var(--bloom-border)]">
        <div className="px-2 mb-3">
          <div className="font-serif italic text-sm text-[var(--bloom-ink)]">{user?.name}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-0.5">{isEtchery ? "Etchery Partner" : "Administrator"}</div>
        </div>
        <button onClick={onLogout} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-[var(--bloom-ink-muted)] hover:text-[var(--bloom-ink)]" data-testid="console-logout">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="club-theme min-h-screen flex" data-testid="club-console">
      {/* Mobile top bar (only when not desktop) */}
      {!isDesktop && (
        <header className="fixed top-0 inset-x-0 z-40 h-14 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)] flex items-center justify-between px-4" data-testid="console-mobile-topbar">
          <button onClick={() => setDrawerOpen(true)} className="p-2 -ml-2" data-testid="console-drawer-open" aria-label="Open menu">
            <Menu size={20} strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img src={BRAND_ASSETS.logo_circle} alt="" className="w-7 h-7 object-contain shrink-0" />
            <div className="font-serif text-base truncate">{activeLabel}</div>
          </div>
          <button onClick={onLogout} className="p-2 -mr-2 text-[var(--bloom-ink-muted)]" aria-label="Sign out" data-testid="console-mobile-logout">
            <LogOut size={18} strokeWidth={1.5} />
          </button>
        </header>
      )}

      {/* Desktop sidebar — mounted ONLY when desktop, so testids stay unique */}
      {isDesktop && (
        <aside
          className="flex w-64 flex-shrink-0 border-r border-[var(--bloom-border)] bg-[var(--bloom-bg)] sticky top-0 self-start h-screen flex-col"
          data-testid="console-sidebar"
        >
          {sidebarBody}
        </aside>
      )}

      {/* Mobile drawer */}
      {!isDesktop && drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-[var(--bloom-ink)]/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="console-drawer-backdrop"
          />
          <aside
            className="fixed top-0 left-0 z-50 w-72 max-w-[85vw] h-full border-r border-[var(--bloom-border)] bg-[var(--bloom-bg)] flex flex-col shadow-2xl animate-[slideIn_.18s_ease-out]"
            data-testid="console-drawer"
          >
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-3 right-3 p-2"
              aria-label="Close menu"
              data-testid="console-drawer-close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            {sidebarBody}
          </aside>
        </>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0" data-testid="console-main">
        <div className={`px-4 sm:px-6 lg:px-12 py-6 lg:py-10 ${!isDesktop ? "pt-20" : ""}`}>
          <Section />
        </div>
      </main>

      <style>{`
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}
